/**
 * Creates a debounced version of an async function.
 * The debounced function will delay execution until after `wait` milliseconds have elapsed
 * since the last time it was invoked.
 *
 * @param fn The async function to debounce
 * @param wait The time in milliseconds to delay execution
 * @returns A debounced version of the input async function that returns a Promise
 */
export default function debounceAsync<T extends (...args: any[]) => Promise<any>>(fn: T, wait: number): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let pending: { resolve: (value: any) => void; reject: (reason: any) => void }[] = [];

	return function (this: any, ...args: Parameters<T>) {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
		}

		return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
			// Superseded callers stay queued and settle with the trailing run's result — every
			// returned promise settles (they used to hang forever when debounced away).
			pending.push({resolve, reject});

			timeoutId = setTimeout(async () => {
				timeoutId = null;
				// This run settles exactly the callers that were waiting for it; calls arriving
				// while fn is in flight belong to the NEXT run (a slow stale run can no longer
				// resolve a newer call's promise with outdated data).
				const settlers = pending;
				pending = [];
				try {
					const result = await fn.apply(this, args);
					settlers.forEach(s => s.resolve(result));
				} catch (err) {
					settlers.forEach(s => s.reject(err));
				}
			}, wait);
		});
	};
}
